import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { type ComponentProps, useState } from 'react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { dateToIso, isoToDate } from '@/lib/utils/date';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DatePickerProps extends FormControlInjectedProps {
  disabled?: boolean;
  /** Disable every date after today. Use for dates that cannot be in the future, e.g. date of birth. */
  disableFuture?: boolean;
  /** Last year offered in the year dropdown. Defaults to the current year. */
  endYear?: number;
  id?: string;
  /** Receives an ISO `YYYY-MM-DD` string. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** First year offered in the year dropdown. */
  startYear?: number;
  /** ISO `YYYY-MM-DD` string, or '' when unset. */
  value: string;
}

/**
 * Props the shared `FormControl` injects through Radix `Slot` onto whatever it
 * wraps. They must reach the trigger button: without them the field renders no
 * error state and announces none to a screen reader, unlike every other input.
 */
type FormControlInjectedProps = Pick<
  ComponentProps<typeof Button>,
  'aria-describedby' | 'aria-invalid' | 'aria-labelledby' | 'name'
>;

const DEFAULT_START_YEAR = 1900;

/** Month the calendar opens on when nothing is selected yet — a sensible midpoint for dates of birth. */
const UNSET_DEFAULT_MONTH = new Date(1980, 0);

/**
 * A date field that opens a calendar below its trigger.
 *
 * Values cross the boundary as ISO `YYYY-MM-DD` strings and are converted with
 * the timezone-safe helpers in `@/lib/utils/date` — never `new Date(string)`.
 *
 * Selecting the already-selected day is a no-op rather than clearing the field:
 * every current caller is a required field. A consumer that needs clearing
 * should get an explicit affordance rather than relying on toggle-off.
 */
export function DatePicker({
  id,
  value,
  onChange,
  disabled = false,
  disableFuture = false,
  placeholder = 'Select date',
  startYear = DEFAULT_START_YEAR,
  endYear = new Date().getFullYear(),
  ...formControlProps
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          // Without this the trigger submits the surrounding form on click.
          type="button"
          variant="outline"
          disabled={disabled}
          // Spread FormControl's aria-*/name so the trigger reflects validation
          // state the way the <input> it replaced did.
          {...formControlProps}
          className={cn(
            'w-full justify-start text-left font-normal',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
            !selected && 'text-muted-foreground',
          )}>
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {selected ? format(selected, 'MMMM d, yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      {/* align="start" + the Radix default side="bottom" anchor the calendar below the trigger's left edge. */}
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          captionLayout="dropdown"
          endMonth={new Date(endYear, 11)}
          startMonth={new Date(startYear, 0)}
          disabled={disableFuture ? { after: new Date() } : undefined}
          defaultMonth={clampToRange(selected ?? UNSET_DEFAULT_MONTH, startYear, endYear)}
          onSelect={(date) => {
            if (!date) return;
            onChange(dateToIso(date));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Keep the opening month inside the navigable range, so it can never land on a blank month. */
function clampToRange(month: Date, startYear: number, endYear: number): Date {
  if (month.getFullYear() < startYear) return new Date(startYear, 0);
  if (month.getFullYear() > endYear) return new Date(endYear, 11);
  return month;
}
